import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Building2, Check, ImageUp, Loader2, Save, Trash2 } from 'lucide-react';
import {
  enregistrerNomPartenaire,
  fetchPartenaires,
  retirerImagePartenaire,
  televerserImagePartenaire,
} from '../services/api';

interface Partenaire {
  code: string;
  libelle: string;
  nom: string | null;
  nomDefaut: string;
  visuel: string | null;
  logo: string | null;
}

/**
 * Mairie et communauté de communes.
 *
 * Elles ne signent pas de bon de parrainage — elles subventionnent, prêtent du
 * matériel, ferment une rue. On leur demande donc ici ce qu'un sponsor dépose
 * de lui-même : un nom, un visuel de bandeau, un logo. La carte de la vitrine
 * est la même, c'est sa place qui change : en tête, devant les entreprises.
 *
 * Tant que le nom est vide, le partenaire ne paraît pas : une carte anonyme en
 * ouverture de page serait pire que son absence.
 */
export default function PartenairesInstitutionnels() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const query = useQuery({ queryKey: ['partenaires'], queryFn: fetchPartenaires });
  const partenaires: Partenaire[] = query.data?.partenaires ?? [];

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['partenaires'] });
    // La vitrine publique lit la même chose : elle doit se remettre à jour
    // sans qu'on ait à recharger la page.
    queryClient.invalidateQueries({ queryKey: ['vitrine-sponsors'] });
  };

  if (query.isPending) {
    return <Loader2 className="w-6 h-6 animate-spin text-primary-600" />;
  }

  return (
    <div className="space-y-4">
      {notice && (
        <div
          className={`flex items-start gap-2 text-sm rounded-lg p-3 ${
            notice.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? (
            <Check className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">
            Fermer
          </button>
        </div>
      )}

      {partenaires.map((p) => (
        <FichePartenaire
          key={p.code}
          partenaire={p}
          onSucces={(message) => {
            setNotice({ type: 'success', message });
            rafraichir();
          }}
          onEchec={(message) => setNotice({ type: 'error', message })}
        />
      ))}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Le visuel sert de bandeau à la carte, le logo se pose dessus — comme pour un sponsor.
        Un partenaire dont le nom reste vide ne paraît pas sur la page publique.
      </p>
    </div>
  );
}

function FichePartenaire({
  partenaire: p,
  onSucces,
  onEchec,
}: {
  partenaire: Partenaire;
  onSucces: (message: string) => void;
  onEchec: (message: string) => void;
}) {
  const [nom, setNom] = useState(p.nom ?? '');

  const enregistrer = useMutation({
    mutationFn: () => enregistrerNomPartenaire(p.code, nom.trim()),
    onSuccess: () => onSucces(`${p.libelle} enregistrée.`),
    onError: (e: Error) => onEchec(e.message),
  });

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-primary-600 shrink-0" />
        <h3 className="font-medium text-gray-900 dark:text-gray-100">{p.libelle}</h3>
      </div>

      <div className="flex gap-2">
        <input
          className="input flex-1"
          value={nom}
          placeholder={p.nomDefaut}
          onChange={(e) => setNom(e.target.value)}
          aria-label={`Nom affiché de ${p.libelle}`}
        />
        <button
          type="button"
          onClick={() => enregistrer.mutate()}
          disabled={enregistrer.isPending || nom.trim() === (p.nom ?? '')}
          className="btn-primary px-3 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {enregistrer.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">Enregistrer</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ChampImage
          code={p.code}
          image="visuel"
          libelle="Visuel (bandeau)"
          url={p.visuel}
          onSucces={onSucces}
          onEchec={onEchec}
        />
        <ChampImage
          code={p.code}
          image="logo"
          libelle="Logo (pastille)"
          url={p.logo}
          onSucces={onSucces}
          onEchec={onEchec}
        />
      </div>
    </section>
  );
}

/**
 * Dépôt d'une image, avec son aperçu.
 *
 * L'aperçu n'est pas un ornement : c'est le seul moyen de vérifier qu'on a
 * déposé le blason dans la pastille et l'affiche dans le bandeau, et non
 * l'inverse.
 */
function ChampImage({
  code,
  image,
  libelle,
  url,
  onSucces,
  onEchec,
}: {
  code: string;
  image: string;
  libelle: string;
  url: string | null;
  onSucces: (message: string) => void;
  onEchec: (message: string) => void;
}) {
  const champ = useRef<HTMLInputElement>(null);

  const televerser = useMutation({
    mutationFn: (fichier: File) => televerserImagePartenaire(code, image, fichier),
    onSuccess: () => onSucces(`${libelle} déposé.`),
    onError: (e: Error) => onEchec(e.message),
  });

  const retirer = useMutation({
    mutationFn: () => retirerImagePartenaire(code, image),
    onSuccess: () => onSucces(`${libelle} retiré.`),
    onError: (e: Error) => onEchec(e.message),
  });

  return (
    <div className="space-y-1.5">
      <p className="text-sm text-gray-600 dark:text-gray-400">{libelle}</p>

      <div className="h-24 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-800">
        {url ? (
          <img src={url} alt={libelle} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-xs text-gray-400">Aucune image</span>
        )}
      </div>

      <div className="flex gap-2">
        <input
          ref={champ}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const fichier = e.target.files?.[0];
            if (fichier) televerser.mutate(fichier);
            // Le champ est vidé pour qu'un second dépôt du même fichier
            // déclenche bien l'événement.
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => champ.current?.click()}
          disabled={televerser.isPending}
          className="btn-outline inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 disabled:opacity-50"
        >
          {televerser.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <ImageUp className="w-3.5 h-3.5" />
          )}
          {url ? 'Remplacer' : 'Déposer'}
        </button>

        {url && (
          <button
            type="button"
            onClick={() => retirer.mutate()}
            disabled={retirer.isPending}
            className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Retirer
          </button>
        )}
      </div>
    </div>
  );
}
